import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  Typography,
  Box,
  Avatar,
  Chip,
  Checkbox,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Groups2Rounded as TeamIcon,
  CheckCircleRounded as CheckIcon,
} from '@mui/icons-material';
import { LEAD_THEME } from '../../utils/lead_helper_v2';

// ── Role badge palettes ─────────────────────────────────────────────────────
const ROLE_BADGE = {
  AD: { label: 'Admin',   color: '#7C3AED', bg: '#F5F3FF', border: '#7C3AED22' },
  MG: { label: 'Manager', color: '#0891B2', bg: '#ECFEFF', border: '#0891B222' },
  AG: { label: 'Agent',   color: '#6B7280', bg: '#F3F4F6', border: '#6B728022' },
};

// ── Avatar palette — 12 distinct hues ────────────────────────────────────────
const AVATAR_PALETTES = [
  { bg: '#DBEAFE', color: '#1E40AF' },
  { bg: '#D1FAE5', color: '#065F46' },
  { bg: '#EDE9FE', color: '#5B21B6' },
  { bg: '#FFEDD5', color: '#9A3412' },
  { bg: '#FEF3C7', color: '#92400E' },
  { bg: '#CFFAFE', color: '#155E75' },
  { bg: '#FCE7F3', color: '#9D174D' },
  { bg: '#FEE2E2', color: '#991B1B' },
  { bg: '#E0E7FF', color: '#3730A3' },
  { bg: '#ECFDF5', color: '#047857' },
  { bg: '#FFF7ED', color: '#C2410C' },
  { bg: '#F0FDFA', color: '#0F766E' },
];

function avatarPalette(name) {
  if (!name) return AVATAR_PALETTES[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTES[Math.abs(h) % AVATAR_PALETTES.length];
}

const getTokens = (themeVariant) => {
  if (themeVariant === 'lead') {
    return {
      accent:       LEAD_THEME.green,
      accentMd:     LEAD_THEME.greenMd,
      accentLight:  LEAD_THEME.greenLt,
      accentSoft:   LEAD_THEME.greenSf,
      border:       LEAD_THEME.border,
      borderLight:  LEAD_THEME.borderL,
      bg:           LEAD_THEME.bg,
      ink:          LEAD_THEME.ink,
      ink2:         LEAD_THEME.ink2,
      muted:        LEAD_THEME.muted,
      white:        LEAD_THEME.white,
    };
  }
  return {
    accent:       'primary.main',
    accentMd:     'primary.dark',
    accentLight:  'primary.50',
    accentSoft:   'action.selected',
    border:       'divider',
    borderLight:  'action.hover',
    bg:           'background.default',
    ink:          'text.primary',
    ink2:         'text.secondary',
    muted:        'text.disabled',
    white:        '#ffffff',
  };
};

const AgentSelect = ({
  agents = [],
  value = [],
  onChange,
  label = 'Select Agent',
  name = 'agent',
  required = false,
  error = '',
  size = 'small',
  variant = 'default',
  multiple = false,
  showAvatar = true,
  clearable = false,
  sx = {},
  helperText,
  themeVariant,
  hideInputLabel = false,
  inputPlaceholder,
  limitTags = -1,
  ...props
}) => {
  const T = getTokens(themeVariant);
  const isLead = themeVariant === 'lead';

  const getLabel = () => {
    switch (variant) {
      case 'assign':       return label || 'Assign to';
      case 'reassign':     return label || 'Reassign to';
      case 'share':        return label || 'Share with';
      case 'participants': return label || 'Add participants';
      default:             return label;
    }
  };

  const getAvatarText = (n) => {
    if (!n) return '?';
    return n.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase();
  };

  // ── Shared style objects ──────────────────────────────────────────────────
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '0.82rem',
      backgroundColor: isLead ? LEAD_THEME.white : undefined,
      transition: 'box-shadow 0.2s, border-color 0.2s',
      '& fieldset': {
        borderColor: isLead ? LEAD_THEME.border : undefined,
      },
      '&:hover fieldset': {
        borderColor: isLead ? LEAD_THEME.green : undefined,
      },
      '&.Mui-focused fieldset': {
        borderWidth: 1.5,
        borderColor: isLead ? LEAD_THEME.green : undefined,
      },
      '&.Mui-focused': {
        boxShadow: isLead ? `0 0 0 3px ${LEAD_THEME.greenSf}` : undefined,
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.82rem',
      '&.Mui-focused': {
        color: isLead ? LEAD_THEME.green : undefined,
      },
    },
  };

  const dropdownSx = {
    '& .MuiAutocomplete-groupLabel': {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: isLead ? LEAD_THEME.bg : undefined,
      color: isLead ? LEAD_THEME.ink2 : undefined,
      fontWeight: 700,
      fontSize: '0.68rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      py: 0.75,
      px: 1.25,
      position: 'sticky',
      top: 0,
      zIndex: 1,
      borderBottom: `1px solid ${isLead ? LEAD_THEME.borderL : '#e5e7eb'}`,
    },
    '& .MuiAutocomplete-paper': {
      borderRadius: '12px',
      border: `1px solid ${isLead ? LEAD_THEME.border : '#e5e7eb'}`,
      boxShadow: '0 12px 40px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.04)',
      overflow: 'hidden',
      mt: 0.5,
    },
  };

  /** Slim scrollbars (listbox + nested scroll areas) */
  const slimScrollSx = {
    scrollbarWidth: 'thin',
    scrollbarColor: isLead
      ? `${LEAD_THEME.border} transparent`
      : 'rgba(0,0,0,0.22) transparent',
    '&::-webkit-scrollbar': {
      width: 5,
      height: 5,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: isLead ? LEAD_THEME.border : 'rgba(0,0,0,0.22)',
      borderRadius: '10px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: isLead ? LEAD_THEME.ink2 : 'rgba(0,0,0,0.35)',
    },
  };

  const listboxSx = {
    maxHeight: '340px',
    py: 0,
    ...slimScrollSx,
    '& .MuiAutocomplete-groupUl': { pt: 0 },
    '& .MuiAutocomplete-option': {
      px: 1.25,
      py: 0.75,
      borderRadius: '6px',
      mx: 0.5,
      my: '2px',
      transition: 'background-color 0.12s',
      '&[aria-selected="true"]': {
        backgroundColor: isLead ? LEAD_THEME.greenSf : undefined,
      },
      '&.Mui-focused': {
        backgroundColor: isLead ? `${LEAD_THEME.greenSf} !important` : undefined,
      },
    },
  };

  const sortedAgents = [...agents].sort((a, b) => {
    const teamA = (a.team_name || 'Other').trim();
    const teamB = (b.team_name || 'Other').trim();
    if (teamA !== teamB) return teamA.localeCompare(teamB);
    const roleOrder = { 'MG': 0, 'AG': 1, 'AD': 2 };
    const roleA = roleOrder[a.role] ?? 3;
    const roleB = roleOrder[b.role] ?? 3;
    if (roleA !== roleB) return roleA - roleB;
    return (a.agent_name || '').localeCompare(b.agent_name || '');
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleMultiChange = (event, newValues) => {
    if (!onChange) return;
    const uniqueValues = [];
    const addedIds = new Set();
    for (const agent of newValues) {
      const agentId = agent.agent_id || agent.id;
      if (agentId && !addedIds.has(agentId.toString())) {
        addedIds.add(agentId.toString());
        uniqueValues.push({ ...agent, id: agent.id || agent.agent_id, agent_id: agent.agent_id || agent.id });
      }
    }
    onChange(uniqueValues.map(a => a.agent_id || a.id), uniqueValues);
  };

  const getSelectedAgents = () => {
    if (!multiple || !value) return [];
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      const uniqueAgents = [];
      const addedIds = new Set();
      value.forEach(agent => {
        if (!agent) return;
        const agentId = agent.agent_id || agent.id;
        if (agentId && !addedIds.has(agentId.toString())) {
          addedIds.add(agentId.toString());
          uniqueAgents.push(agent);
        }
      });
      return uniqueAgents;
    }
    if (!Array.isArray(value)) return [];
    const validValues = value.filter(id => id !== null && id !== undefined);
    try {
      if (validValues.length === 0) return [];
      const uniqueIds = Array.from(new Set(validValues.map(id => id?.toString())));
      return uniqueIds
        .map(id => id ? agents.find(a => (a?.agent_id?.toString() === id) || (a?.id?.toString() === id)) : null)
        .filter(Boolean);
    } catch { return []; }
  };

  const getSelectedAgent = () => {
    if (multiple || !value) return null;
    if (typeof value === 'object' && (value.agent_id || value.id)) return value;
    return agents.find(a =>
      (a.agent_id && a.agent_id.toString() === value.toString()) ||
      (a.id && a.id.toString() === value.toString())
    ) || null;
  };

  // ── Shared renderers ──────────────────────────────────────────────────────
  const renderRoleBadge = (role, isAdmin) => {
    if (isAdmin || role === 'AD') {
      const b = ROLE_BADGE.AD;
      return (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', height: 18, px: 0.75,
          borderRadius: '5px', bgcolor: b.bg, border: `1px solid ${b.border}`,
        }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: b.color, lineHeight: 1 }}>
            {b.label}
          </Typography>
        </Box>
      );
    }
    if (role === 'MG') {
      const b = ROLE_BADGE.MG;
      return (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', height: 18, px: 0.75,
          borderRadius: '5px', bgcolor: b.bg, border: `1px solid ${b.border}`,
        }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: b.color, lineHeight: 1 }}>
            {b.label}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderAgentAvatar = (agentName, w = 30, h = 30, fs = '0.72rem') => {
    const pal = avatarPalette(agentName);
    return (
      <Avatar sx={{
        width: w, height: h, fontSize: fs, fontWeight: 600,
        bgcolor: pal.bg, color: pal.color,
        border: `1.5px solid ${pal.color}20`,
      }}>
        {getAvatarText(agentName)}
      </Avatar>
    );
  };

  const renderOptionContent = (option, selected) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 0.25 }}>
      {showAvatar && (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          {renderAgentAvatar(option.agent_name, 32, 32, '0.72rem')}
          {selected && (
            <CheckIcon sx={{
              position: 'absolute', bottom: -2, right: -2,
              fontSize: 14, color: isLead ? LEAD_THEME.green : 'primary.main',
              bgcolor: '#fff', borderRadius: '50%',
            }} />
          )}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{
            fontSize: '0.78rem', fontWeight: selected ? 600 : 500,
            color: selected && isLead ? LEAD_THEME.green : undefined,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {option.agent_name}
          </Typography>
          {renderRoleBadge(option.role, option.is_admin)}
        </Box>
        {option.email && (
          <Typography sx={{ fontSize: '0.66rem', color: isLead ? LEAD_THEME.muted : 'text.secondary', lineHeight: 1.2 }} noWrap>
            {option.email}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderSelectedChip = (option, index, getTagProps) => {
    const pal = avatarPalette(option.agent_name);
    const { key: tagKey, ...tagProps } = getTagProps({ index });
    return (
      <Chip
        key={tagKey ?? option.agent_id ?? option.id ?? index}
        label={option.agent_name}
        {...tagProps}
        avatar={showAvatar ? renderAgentAvatar(option.agent_name, 22, 22, '0.62rem') : undefined}
        size="small"
        sx={{
          borderRadius: '8px',
          height: 26,
          bgcolor: pal.bg,
          color: pal.color,
          border: `1px solid ${pal.color}22`,
          fontWeight: 500,
          fontSize: '0.72rem',
          transition: 'all 0.15s',
          '& .MuiChip-label': { px: 0.75 },
          '& .MuiChip-deleteIcon': {
            color: pal.color,
            opacity: 0.4,
            fontSize: '0.85rem',
            '&:hover': { opacity: 0.8, color: '#EF4444' },
          },
          '& .MuiChip-avatar': { ml: '3px' },
          '&:hover': { bgcolor: `${pal.color}18` },
        }}
      />
    );
  };

  const groupByTeam = (option) => (option.team_name || 'Other').trim();

  const isOptionEqual = (option, val) => {
    if (!val) return false;
    return (
      (option.id && val.id && option.id === val.id) ||
      (option.agent_id && val.agent_id && option.agent_id === val.agent_id) ||
      (option.id && val.agent_id && option.id === val.agent_id) ||
      (option.agent_id && val.id && option.agent_id === val.id)
    );
  };

  /** Single-select only: while a value is chosen and the list is closed, lock the field so random text cannot be appended to the label. */
  const [singleListboxOpen, setSingleListboxOpen] = useState(false);

  // ── Multi-select ──────────────────────────────────────────────────────────
  if (multiple) {
    const toggleTeamSelection = (teamName, event) => {
      event?.stopPropagation?.();
      event?.preventDefault?.();
      const teamAgents = sortedAgents.filter(
        (a) => (a.team_name || 'Other').trim() === teamName
      );
      if (!teamAgents.length) return;
      const current = getSelectedAgents();
      const selectedIds = new Set(
        current.map((a) => (a.agent_id || a.id).toString())
      );
      const allSelected = teamAgents.every((a) =>
        selectedIds.has((a.agent_id || a.id).toString())
      );
      if (allSelected) {
        const remaining = current.filter(
          (a) => (a.team_name || 'Other').trim() !== teamName
        );
        handleMultiChange(null, remaining);
      } else {
        const merged = [...current];
        const seen = new Set(merged.map((a) => (a.agent_id || a.id).toString()));
        teamAgents.forEach((a) => {
          const id = (a.agent_id || a.id)?.toString();
          if (id && !seen.has(id)) {
            seen.add(id);
            merged.push({
              ...a,
              id: a.id || a.agent_id,
              agent_id: a.agent_id || a.id,
            });
          }
        });
        handleMultiChange(null, merged);
      }
    };

    return (
      <FormControl fullWidth error={!!error} required={required} sx={sx} {...props}>
        <Autocomplete
          multiple
          limitTags={limitTags}
          id={`agent-multi-select-${name}`}
          options={sortedAgents}
          value={getSelectedAgents()}
          onChange={handleMultiChange}
          getOptionLabel={(o) => o.agent_name}
          groupBy={groupByTeam}
          isOptionEqualToValue={isOptionEqual}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField
              {...params}
              label={hideInputLabel ? undefined : getLabel()}
              hiddenLabel={hideInputLabel}
              placeholder={hideInputLabel ? (inputPlaceholder || 'Search agents…') : undefined}
              error={!!error}
              helperText={error || helperText}
              size={size}
              sx={inputSx}
            />
          )}
          renderOption={(listProps, option, { selected }) => {
            const { key, ...restListProps } = listProps;
            return (
              <li key={key} {...restListProps}>
                {renderOptionContent(option, selected)}
              </li>
            );
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => renderSelectedChip(option, index, getTagProps))
          }
          renderGroup={(params) => {
            const teamName = params.group;
            const teamAgents = sortedAgents.filter(
              (a) => (a.team_name || 'Other').trim() === teamName
            );
            const selectedIds = new Set(
              getSelectedAgents().map((a) => (a.agent_id || a.id).toString())
            );
            let selectedInTeam = 0;
            teamAgents.forEach((a) => {
              if (selectedIds.has((a.agent_id || a.id).toString())) selectedInTeam += 1;
            });
            const allTeamSelected =
              teamAgents.length > 0 && selectedInTeam === teamAgents.length;
            const someTeamSelected =
              selectedInTeam > 0 && selectedInTeam < teamAgents.length;

            return (
              <li key={params.key}>
                <Box
                  component="div"
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                    backgroundColor: isLead ? LEAD_THEME.bg : undefined,
                    color: isLead ? LEAD_THEME.ink2 : undefined,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    py: 0.5,
                    pl: 1.25,
                    pr: 0.25,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    borderBottom: `1px solid ${isLead ? LEAD_THEME.borderL : '#e5e7eb'}`,
                  }}
                >
                  <TeamIcon sx={{ fontSize: 13, opacity: 0.5, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      letterSpacing: 'inherit',
                      flex: 1,
                      minWidth: 0,
                    }}
                    noWrap
                  >
                    {params.group}
                  </Typography>
                  <Checkbox
                    size="small"
                    checked={allTeamSelected}
                    indeterminate={someTeamSelected}
                    onChange={(e) => toggleTeamSelection(teamName, e)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      p: 0.35,
                      ml: 'auto',
                      flexShrink: 0,
                      color: isLead ? LEAD_THEME.green : undefined,
                      '&.Mui-checked': { color: isLead ? LEAD_THEME.green : undefined },
                      '&.MuiCheckbox-indeterminate': { color: isLead ? LEAD_THEME.green : undefined },
                    }}
                    inputProps={{
                      'aria-label': `Select all agents in ${teamName}`,
                    }}
                  />
                </Box>
                <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
              </li>
            );
          }}
          ListboxProps={{ sx: listboxSx }}
          sx={dropdownSx}
          noOptionsText={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <Typography sx={{ fontSize: '0.78rem', color: isLead ? LEAD_THEME.muted : 'text.secondary' }}>
                {getSelectedAgents().length >= sortedAgents.length && sortedAgents.length > 0
                  ? 'All agents selected'
                  : 'No agents found'}
              </Typography>
            </Box>
          }
        />
      </FormControl>
    );
  }

  // ── Single-select ─────────────────────────────────────────────────────────
  const selectedAgent = getSelectedAgent();

  return (
    <FormControl fullWidth error={!!error} required={required} sx={sx} {...props}>
      <Autocomplete
        id={`agent-single-select-${name}`}
        options={sortedAgents}
        value={selectedAgent}
        onOpen={() => setSingleListboxOpen(true)}
        onClose={() => setSingleListboxOpen(false)}
        onChange={(event, newValue) => {
          if (onChange) {
            const agentId = newValue ? (newValue.agent_id || newValue.id) : '';
            onChange(agentId, newValue);
          }
        }}
        getOptionLabel={(o) => o.agent_name || ''}
        groupBy={groupByTeam}
        isOptionEqualToValue={isOptionEqual}
        filterOptions={(options, params) => {
          const searchText = params.inputValue.toLowerCase().trim();
          if (!searchText) return options;
          return options.filter((o) => {
            const n = (o.agent_name || '').toLowerCase();
            const e = (o.email || '').toLowerCase();
            return n.includes(searchText) || e.includes(searchText);
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={hideInputLabel ? undefined : getLabel()}
            hiddenLabel={hideInputLabel}
            placeholder={hideInputLabel ? (inputPlaceholder || 'Search agents…') : undefined}
            error={!!error}
            helperText={error || helperText}
            size={size}
            required={required}
            sx={inputSx}
            inputProps={{
              ...params.inputProps,
              // Do not use Autocomplete `readOnly` — it hides the clear icon. Lock only the text field.
              readOnly: Boolean(selectedAgent) && !singleListboxOpen,
            }}
          />
        )}
        renderOption={(listProps, option) => {
          const { key, ...restListProps } = listProps;
          return (
            <li key={key} {...restListProps}>
              {renderOptionContent(option, false)}
            </li>
          );
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: '6px',
              backgroundColor: isLead ? LEAD_THEME.bg : undefined,
              color: isLead ? LEAD_THEME.ink2 : undefined,
              fontWeight: 700, fontSize: '0.68rem',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              py: 0.75, px: 1.25,
              position: 'sticky', top: 0, zIndex: 1,
              borderBottom: `1px solid ${isLead ? LEAD_THEME.borderL : '#e5e7eb'}`,
            }}>
              <TeamIcon sx={{ fontSize: 13, opacity: 0.5 }} />
              <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit', letterSpacing: 'inherit' }}>
                {params.group}
              </Typography>
            </Box>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderTags={() => null}
        ListboxProps={{ sx: listboxSx }}
        sx={dropdownSx}
        noOptionsText={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
            <Typography sx={{ fontSize: '0.78rem', color: isLead ? LEAD_THEME.muted : 'text.secondary' }}>
              No agents found
            </Typography>
          </Box>
        }
        clearOnBlur={false}
        clearOnEscape={clearable}
        disableClearable={!clearable}
      />
    </FormControl>
  );
};

AgentSelect.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      agent_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      agent_name: PropTypes.string.isRequired,
      team_name: PropTypes.string,
      email: PropTypes.string,
    })
  ),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  helperText: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['default', 'assign', 'reassign', 'share', 'participants']),
  multiple: PropTypes.bool,
  showAvatar: PropTypes.bool,
  clearable: PropTypes.bool,
  themeVariant: PropTypes.oneOf(['lead']),
  sx: PropTypes.object,
  hideInputLabel: PropTypes.bool,
  inputPlaceholder: PropTypes.string,
  limitTags: PropTypes.number,
};

export default AgentSelect;